/**
 * Botón de header para avisar al almacén de que hay pedidos por preparar
 * Solo visible para administradores
 */
import React, { useState, useContext } from "react";
import { Box, IconButton } from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import AuthService from "../../../../services/auth.service";
import Roles from "../../../../config/roles";
import Toasts from "../../../../components/notificacion/Toasts";
import { StateContext } from "../../../../components/context/StateProvider";
import { getErrorMessage } from "../../../../utils/ErrorUtils";
import VentasService from "../../api/ventas.api";
import { ModalConfirmacion } from "../modals";

/**
 * Botón que dispara el aviso de preparación al turno de almacén.
 * @param {boolean} compacto - Versión reducida para el header plegado
 */
const BotonAvisarPreparacion = ({ compacto = false }) => {
    const { state, updateState } = useContext(StateContext);
    const [confirmar, setConfirmar] = useState(false);
    const [enviando, setEnviando] = useState(false);

    const esAdmin = AuthService.getUserRoles()?.includes(Roles.ADMIN.role);

    if (!esAdmin) {
        return null;
    }

    const avisar = async () => {
        setEnviando(true);

        try {
            const { avisados } = await VentasService.avisarPreparacion();

            // El endpoint responde 200 con avisados: 0 si nadie tiene rol almacén.
            // No es un error, pero conviene que quien pulsa sepa que no ha llegado a nadie
            const mensaje = avisados > 0
                ? `Aviso enviado a ${avisados} ${avisados === 1 ? 'persona' : 'personas'} de almacén`
                : 'No hay nadie con rol almacén a quien avisar';

            const toasts = Toasts.generateToast(mensaje, avisados > 0 ? 'success' : 'warning');
            updateState({ toasts: [...state.toasts, toasts] });
            setConfirmar(false);
        } catch (error) {
            const toasts = Toasts.generateToast(getErrorMessage(error, 'No se pudo enviar el aviso'), 'error');
            updateState({ toasts: [...state.toasts, toasts] });
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
                size="small"
                title="Avisar al almacén de que hay pedidos por preparar"
                onClick={() => setConfirmar(true)}
                sx={compacto ? { p: 0.5 } : undefined}
            >
                <CampaignIcon sx={{ fontSize: compacto ? 19 : 24, color: '#6c757d' }} />
            </IconButton>

            {/* DECISION: se pide confirmación porque el endpoint no tiene límite de
                frecuencia y avisa a todo el turno: cada pulsación es una notificación
                para cada persona de almacén */}
            <ModalConfirmacion
                show={confirmar}
                onHide={() => setConfirmar(false)}
                onConfirm={avisar}
                loading={enviando}
                title="Avisar al almacén"
                titleMobile="Avisar"
                text="Se enviará una notificación a <strong>todo el personal de almacén</strong> avisando de que hay pedidos pendientes de preparar."
                confirmLabel="Enviar aviso"
                confirmLabelMobile="Enviar"
                icon={CampaignIcon}
                confirmIcon={CampaignIcon}
            />
        </Box>
    );
};

export default BotonAvisarPreparacion;
